/**
 * Phone teams from the console: adding someone to a village team and setting a PIN for a
 * phone-only worker. The PIN rules match the server (uc-core pin-hash.util: 4–6 digits, not one
 * repeated digit, not a straight run up or down) so the office sees the problem before saving.
 */

export type VillageTeamRole = 'village_operator' | 'supervisor'

export interface VillageTeamForm {
  mode: 'existing' | 'new'
  role: VillageTeamRole
  userId: string | null
  firstName: string
  lastName: string
  contactNo: string
}

export type VillageTeamBody =
  | { role: VillageTeamRole; userId: string }
  | { role: VillageTeamRole; firstName: string; lastName?: string; contactNo?: string }

export const emptyVillageTeamForm = (): VillageTeamForm => ({
  mode: 'existing',
  role: 'village_operator',
  userId: null,
  firstName: '',
  lastName: '',
  contactNo: '',
})

export function validateVillageTeamForm(
  f: VillageTeamForm,
  onTeam: ReadonlySet<string> = new Set(),
): string | null {
  if (f.mode === 'existing') {
    if (!f.userId) return 'Choose a person'
    if (onTeam.has(f.userId)) return 'Already on this team'
    return null
  }
  if (!f.firstName.trim()) return 'Type the first name'
  if (f.contactNo.trim() && !/^\+?[\d\s-]{6,20}$/.test(f.contactNo.trim()))
    return 'Phone number: digits only, e.g. 9812345678'
  return null
}

/** Form → request body: an existing user, or a new one with blank fields left out. */
export function villageTeamBody(f: VillageTeamForm): VillageTeamBody {
  if (f.mode === 'existing') return { role: f.role, userId: f.userId! }
  const lastName = f.lastName.trim()
  const contactNo = f.contactNo.replace(/[\s-]/g, '')
  return {
    role: f.role,
    firstName: f.firstName.trim(),
    ...(lastName ? { lastName } : {}),
    ...(contactNo ? { contactNo } : {}),
  }
}

/** Why a PIN would be refused, or null when it is fine. */
export function pinProblem(pin: string, again: string): string | null {
  if (!/^\d{4,6}$/.test(pin)) return '4 to 6 digits'
  if (/^(\d)\1+$/.test(pin)) return 'Too easy to guess: all the same digit'
  const d = pin.split('').map(Number)
  const up = d.every((x, i) => i === 0 || x === (d[i - 1]! + 1) % 10)
  const down = d.every((x, i) => i === 0 || x === (d[i - 1]! + 9) % 10)
  if (up || down) return 'Too easy to guess: digits in a row'
  if (pin !== again) return 'The two PINs are not the same'
  return null
}
